import { CoreFieldProps, CoreFieldValue, AddressFieldValue, SchemaFactoryV2, formatPhoneNumber } from "@ucl/library";
import { get } from "lodash";
import moment from "moment";
import { COMPLEX_FIELD_KEYS } from "@/constants/fieldKeys";
import { GovernmentIdType, ReasonPhysicalAddressIsDifferentThanIdAddressType } from "@/types/enums";

export interface FieldSchemaData {
  label: string;
  value: CoreFieldValue;
  fieldKey: string;
  fieldPath?: string;
  type?: string;
  formattedValue?: string;
  fieldSchema?: any; // Add fieldSchema to access isPercentage and decimals
}

export interface GetFieldSchemaDataOptions {
  modelName?: string;
  fallbackLabel?: string;
}

// Define the types for the field configuration
export interface FieldConfig {
  fieldProps: string;
  complexFieldValue?: {
    hiddenFields?: string[];
    [key: string]: unknown;
  };
}

export type FieldInput = string | FieldConfig;

/**
 * Formats a value based on its type and content
 * @param value - The value to format
 * @param type - The field type
 * @param fieldSchema - The field schema (optional, used for enum lookups)
 * @returns Formatted string representation
 */
function formatValue(value: CoreFieldValue, type?: string, fieldSchema?: any): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (type === "PhoneNumber") {
    return formatPhoneNumber(value as string);
  }

  // Handle address objects
  if (type === "SmartAddress" || type === "SimpleAddress" || isAddressObject(value)) {
    return formatAddress(value as AddressFieldValue);
  }

  // Handle dates
  if (type === "Date" || type === "DateV2" || type === "Calendar") {
    return formatDate(value as string);
  }

  // Handle Social Security Number
  if (type === "SocialSecurityNumber") {
    return formatSocialSecurityNumber(value as string);
  }

  // Handle Social Security Number V2
  if (type === "SocialSecurityNumberV2") {
    return formatSocialSecurityNumber(value as string);
  }

  // Handle Employer Identification Number
  if (type === "EmployerIdentificationNumber") {
    return formatEmployerIdentificationNumber(value as string);
  }

  // Handle currency
  if (type === "Currency") {
    return formatCurrency(value as number);
  }

  // Handle percentage fields
  if (fieldSchema?.isPercentage && typeof value === "number") {
    const decimals = fieldSchema.decimals || 2; // Default to 2 decimal places if not specified
    return `${(value as number).toFixed(decimals)}%`;
  }

  // Handle enum values
  if (isEnumType(type) && fieldSchema?.optionValues) {
    return formatEnumValue(value, fieldSchema.optionValues);
  }

  // Handle booleans
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  // Handle numbers
  if (typeof value === "number") {
    return value.toString();
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  // Handle objects (try to get a meaningful string representation)
  if (typeof value === "object") {
    return formatObject(value as Record<string, unknown>);
  }

  // Default to string conversion
  return String(value);
}

/**
 * Checks if a field type is an enum type
 */
function isEnumType(type?: string): boolean {
  return (
    type === "EnumRadio" ||
    type === "EnumCheckbox" ||
    type === "EnumSingleSelect" ||
    type === "EnumMultiSelect" ||
    type === "EnumAutocomplete"
  );
}

/**
 * Formats an enum value by looking up its label from the option values
 */
function formatEnumValue(value: CoreFieldValue, optionValues: any[]): string {
  if (value === null || value === undefined) {
    return "";
  }

  // Find the option that matches the value
  const option = optionValues.find((opt) => opt.value === value);
  if (option) {
    return option.label;
  }

  // Fallback to the value itself if no matching option found
  return String(value);
}

/**
 * Checks if a value is an address object
 */
function isAddressObject(value: unknown): value is AddressFieldValue {
  return (
    value !== null &&
    typeof value === "object" &&
    ((value as AddressFieldValue).street !== undefined ||
      (value as AddressFieldValue).city !== undefined ||
      (value as AddressFieldValue).state !== undefined ||
      (value as AddressFieldValue).zip !== undefined ||
      (value as AddressFieldValue).fullAddress !== undefined)
  );
}

/**
 * Formats an address object into a readable string
 */
function formatAddress(address: AddressFieldValue): string {
  if (!address) return "";

  // If there's a pre-formatted full address, use it
  if (address.fullAddress) {
    return address.fullAddress;
  }

  // If there are pre-formatted parts, use them
  if (address.addressStreet1And2_AsString && address.addressCityStateAndZip_AsString) {
    return `${address.addressStreet1And2_AsString}, ${address.addressCityStateAndZip_AsString}`;
  }

  // Build address from individual components
  const parts: string[] = [];

  if (address.street) {
    parts.push(address.street);
  }
  if (address.street2) {
    parts.push(address.street2);
  }

  const cityStateZip: string[] = [];
  if (address.city) {
    cityStateZip.push(address.city);
  }
  if (address.state_AsString || address.state) {
    cityStateZip.push(address.state_AsString || String(address.state));
  }
  if (address.zip) {
    cityStateZip.push(address.zip);
  }

  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(", "));
  }

  return parts.join(", ");
}

/**
 * Formats a date string into a readable format
 */
function formatDate(dateString: string): string {
  if (!dateString) return "";

  try {
    const date = moment(dateString);
    if (!date.isValid()) return dateString;

    // Format as M/D/YYYY
    return date.format("M/D/YYYY");
  } catch {
    return dateString;
  }
}

/**
 * Formats an object into a readable string
 */
function formatObject(obj: Record<string, unknown>): string {
  if (!obj) return "";

  // Try to find meaningful string properties
  const stringProps = Object.entries(obj)
    .filter(([, value]) => typeof value === "string" && value.trim())
    .map(([key, value]) => `${key}: ${value}`);

  if (stringProps.length > 0) {
    return stringProps.join(", ");
  }

  // Fallback to JSON string (truncated)
  const jsonStr = JSON.stringify(obj);
  return jsonStr.length > 100 ? jsonStr.substring(0, 100) + "..." : jsonStr;
}

/**
 * Formats a Social Security Number for display
 */
function formatSocialSecurityNumber(ssn: string): string {
  if (!ssn) return "";

  // Remove any non-digit characters
  const digits = ssn.replace(/\D/g, "");

  if (digits.length === 9) {
    // Format as XXX-XX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } else if (digits.length > 0) {
    // If not exactly 9 digits, just return the digits
    return digits;
  }

  return ssn;
}

/**
 * Formats an Employer Identification Number for display
 */
function formatEmployerIdentificationNumber(ein: string): string {
  if (!ein) return "";

  // Remove any non-digit characters
  const digits = ein.replace(/\D/g, "");

  if (digits.length === 9) {
    // Format as XX-XXXXXXX
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } else if (digits.length > 0) {
    // If not exactly 9 digits, just return the digits
    return digits;
  }

  return ein;
}

/**
 * Formats a currency value with two decimal places
 */
function formatCurrency(value: number): string {
  if (value === null || value === undefined) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Extracts field schema data (label and value) from fieldProps using the schema factory
 * @param fieldProps - Either a string field key or a CoreFieldProps object
 * @param formValue - The form data object containing the field values
 * @param schemaFactory - The schema factory instance
 * @param options - Optional configuration
 * @returns FieldSchemaData object with label and value
 */
export function getFieldSchemaData(
  fieldProps: string | Partial<CoreFieldProps>,
  formValue: Record<string, unknown> | object,
  schemaFactory: SchemaFactoryV2,
  options: GetFieldSchemaDataOptions = {}
): FieldSchemaData {
  const { modelName = "FinancialApplicationExpressionSchema", fallbackLabel } = options;

  // Extract field key and other properties
  let fieldKey: string;
  let existingLabel: string | undefined;
  let existingValue: CoreFieldValue | undefined;

  if (typeof fieldProps === "string") {
    // fieldProps is a string field key
    fieldKey = fieldProps;
  } else {
    // fieldProps is a CoreFieldProps object
    fieldKey = fieldProps.fieldKey || "";
    existingLabel = fieldProps.label as string;

    // Check if fieldProps has a value property (from BaseFieldProps)
    if ("value" in fieldProps) {
      existingValue = (fieldProps as Record<string, CoreFieldValue>).value;
    }
  }

  if (!fieldKey) {
    throw new Error("Field key is required to get field schema data");
  }

  // Try to get the field schema to extract the label and fieldPath
  let label = fallbackLabel || fieldKey; // fallback to field key if schema lookup fails
  let type: string | undefined;
  let fieldPath: string | undefined;
  let fieldSchema: any; // Declare fieldSchema here

  try {
    fieldSchema = schemaFactory.getFieldSchemaByName(modelName, fieldKey);
    label = (fieldSchema.label as string) || existingLabel || fallbackLabel || fieldKey;
    type = fieldSchema.type_AsString;
    fieldPath = fieldSchema.fieldPath;
  } catch (error) {
    console.warn(`Could not get field schema for ${fieldKey}:`, error);
    // Use existing label if available, otherwise fallback
    label = existingLabel || fallbackLabel || fieldKey;
  }

  // Get the value from formValue using fieldPath
  let value: CoreFieldValue = existingValue;
  if (!value && fieldPath && formValue) {
    try {
      value = get(formValue as Record<string, unknown>, fieldPath);
    } catch (error) {
      console.warn(`Could not get value for field ${fieldKey} at path ${fieldPath}:`, error);
    }
  }

  // Format the value for display
  const formattedValue = formatValue(value, type, fieldSchema);

  const result = {
    label,
    value,
    fieldKey,
    fieldPath,
    type,
    formattedValue,
    fieldSchema, // Add fieldSchema to the result
  };

  return result;
}

/**
 * Extracts field schema data for multiple fields, expanding complex fields and address fields into their sub-fields
 * @param fieldInputs - Array of field inputs (string field keys or FieldConfig objects)
 * @param formValue - The form data object containing the field values
 * @param schemaFactory - The schema factory instance
 * @param options - Optional configuration
 * @returns Array of FieldSchemaData objects (may be longer than input due to complex field and address field expansion)
 */
export function getFieldSchemaDataList(
  fieldInputs: FieldInput[],
  formValue: Record<string, unknown> | object,
  schemaFactory: SchemaFactoryV2,
  options: GetFieldSchemaDataOptions = {}
): FieldSchemaData[] {
  const { modelName = "FinancialApplicationExpressionSchema" } = options;

  // First, expand any complex field keys and address fields into their individual sub-fields
  const expandedFieldKeys: string[] = [];
  const fieldConfigs: Map<string, FieldConfig> = new Map();

  for (const fieldInput of fieldInputs) {
    let fieldKey: string;
    let fieldConfig: FieldConfig | undefined;

    if (typeof fieldInput === "string") {
      fieldKey = fieldInput;
    } else {
      fieldKey = fieldInput.fieldProps;
      fieldConfig = fieldInput;
    }

    if (fieldKey) {
      // Check if this is a complex field that needs expansion
      if (isComplexFieldKey(fieldKey)) {
        const subFields = getComplexFieldSubFields(fieldKey, modelName);

        // If we have a fieldConfig with hiddenFields, filter out the hidden fields
        if (fieldConfig?.complexFieldValue?.hiddenFields) {
          const hiddenFields = fieldConfig.complexFieldValue.hiddenFields;
          const filteredSubFields = subFields.filter((subField) => {
            // Extract the field name from the sub-field key
            const subFieldName = subField.split("__").pop();
            return !hiddenFields.includes(subFieldName || "");
          });
          expandedFieldKeys.push(...filteredSubFields);
        } else {
          expandedFieldKeys.push(...subFields);
        }

        // Store the field config for later use if needed
        if (fieldConfig) {
          fieldConfigs.set(fieldKey, fieldConfig);
        }
      } else {
        expandedFieldKeys.push(fieldKey);
      }
    }
  }

  // Now process the expanded field keys and handle address field expansion
  const result: FieldSchemaData[] = [];

  for (const fieldKey of expandedFieldKeys) {
    const fieldData = getFieldSchemaData(fieldKey, formValue, schemaFactory, options);

    // Check if this field contains an address value that should be expanded
    if (isAddressField(fieldData)) {
      const addressComponents = getAddressFieldComponents(fieldData);
      result.push(...addressComponents);
    } else {
      result.push(fieldData);
    }
  }

  return result;
}

/**
 * Checks if a field key represents a complex field
 */
function isComplexFieldKey(fieldKey: string): boolean {
  // Complex fields are the parent fields that contain sub-fields
  // Examples: "FinancialApplicationExpressionSchema__PersonInformation"
  // vs "FinancialApplicationExpressionSchema__PersonInformation__FirstName"

  // Check if this is a complex field by looking for the pattern:
  // - Contains exactly one "__" (model__field)
  // - Does NOT contain "__" followed by another field name (model__field__subfield)
  const parts = fieldKey.split("__");

  // If it has exactly 2 parts (model__field), it's likely a complex field
  // If it has 3+ parts (model__field__subfield), it's a sub-field
  return parts.length === 2;
}

/**
 * Checks if a field contains an address value that should be expanded
 */
function isAddressField(fieldData: FieldSchemaData): boolean {
  return (
    fieldData.type === "SmartAddress" ||
    fieldData.type === "SimpleAddress" ||
    isAddressObject(fieldData.value)
  );
}

/**
 * Gets the individual address components for an address field
 * @param fieldData - The field data containing the address
 * @param formValue - The form data object
 * @param schemaFactory - The schema factory instance
 * @param options - Optional configuration
 * @returns Array of FieldSchemaData objects for each address component
 */
function getAddressFieldComponents(fieldData: FieldSchemaData): FieldSchemaData[] {
  const address = fieldData.value as AddressFieldValue;
  if (!address) {
    return []; // Return original field if no address value
  }

  const components: FieldSchemaData[] = [];
  const baseFieldKey = fieldData.fieldKey;
  const baseFieldPath = fieldData.fieldPath;

  // Define address component mappings
  const addressComponents = [
    { key: "street", label: "Street Address", path: "street" },
    { key: "street2", label: "Street Address 2", path: "street2" },
    { key: "city", label: "City", path: "city" },
    { key: "state_AsString", label: "State", path: "state_AsString" },
    { key: "zip", label: "ZIP Code", path: "zip" },
  ];

  for (const component of addressComponents) {
    const value = address[component.key as keyof AddressFieldValue];
    
    if (value !== undefined && value !== null && value !== "") {
      const componentFieldKey = `${baseFieldKey}__${component.key}`;
      const componentFieldPath = baseFieldPath ? `${baseFieldPath}.${component.path}` : undefined;

      components.push({
        label: component.label,
        value,
        fieldKey: componentFieldKey,
        fieldPath: componentFieldPath,
        type: "string",
        formattedValue: String(value),
      });
    }
  }

  // If no individual components were found, return the original field
  return components.length > 0 ? components : [];
}

/**
 * Gets the sub-fields for a complex field using the hardcoded COMPLEX_FIELD_KEYS
 * @param complexFieldKey - The complex field key to get sub-fields for
 * @param modelName - The model name
 * @returns Array of sub-field keys
 */
function getComplexFieldSubFields(
  complexFieldKey: string,
  modelName: string = "FinancialApplicationExpressionSchema"
): string[] {
  // Extract the field name from the complex field key (remove the model prefix)
  const fieldName = complexFieldKey.replace(`${modelName}__`, "");

  // Map the field name to the corresponding COMPLEX_FIELD_KEYS structure
  const complexFieldMapping: Record<string, keyof typeof COMPLEX_FIELD_KEYS> = {
    PersonInformation: "PERSON_INFORMATION",
    GovernmentIdInformation: "GOVERNMENT_ID_INFORMATION",
    EmploymentInformation: "EMPLOYMENT_INFORMATION",
    DigitalBankingInformation: "DIGITAL_BANKING_INFORMATION",
    DebitCardInformation: "DEBIT_CARD_INFORMATION",
    SecurityQuestionsInformation: "SECURITY_QUESTIONS_INFORMATION",
    OrganizationInformation: "ORGANIZATION_INFORMATION",
    OrganizationContactInformation: "ORGANIZATION_CONTACT_INFORMATION",
    PersonalAccountAcceptableUse: "PERSONAL_ACCOUNT_ACCEPTABLE_USE",
    OrganizationAccountAcceptableUse: "ORGANIZATION_ACCOUNT_ACCEPTABLE_USE",
    PersonalAccountRiskReview: "PERSONAL_ACCOUNT_RISK_REVIEW",
    LawFirmOrganizationAccountRiskReview: "LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW",
    NonLawFirmOrganizationAccountRiskReview: "NON_LAW_FIRM_ORGANIZATION_ACCOUNT_RISK_REVIEW",
  };

  const complexFieldKeyName = complexFieldMapping[fieldName];

  if (complexFieldKeyName && COMPLEX_FIELD_KEYS[complexFieldKeyName]) {
    // Get the sub-field names from COMPLEX_FIELD_KEYS
    const subFieldNames = Object.values(COMPLEX_FIELD_KEYS[complexFieldKeyName]);

    // Build the full field keys by combining the complex field key with each sub-field name
    const subFieldKeys = subFieldNames.map((subFieldName) => `${complexFieldKey}__${subFieldName}`);

    return subFieldKeys;
  }

  // Fallback to original key if not found
  console.warn(`Could not get sub-fields for ${complexFieldKey}:`, complexFieldKeyName);
  return [complexFieldKey];
}

/**
 * Determines which government ID fields should be hidden based on the government ID information
 * @param governmentIdInformation - The government ID information object
 * @returns Array of field keys that should be hidden
 */
export function getHiddenGovernmentIdFields(governmentIdInformation?: {
  governmentIdType?: GovernmentIdType;
  isPhysicalAddressDifferentThanIdAddress?: boolean;
  reasonPhysicalAddressIsDifferentThanIdAddress?: ReasonPhysicalAddressIsDifferentThanIdAddressType;
}): string[] {
  const hiddenFields: string[] = [];

  if (!governmentIdInformation) {
    return hiddenFields;
  }

  const { governmentIdType, isPhysicalAddressDifferentThanIdAddress, reasonPhysicalAddressIsDifferentThanIdAddress } = governmentIdInformation;
  
  if (governmentIdType !== GovernmentIdType.StateIssuedID) {
    hiddenFields.push(COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_STATE);
  }
  
  if (governmentIdType !== GovernmentIdType.Passport) {
    hiddenFields.push(COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.GOVERNMENT_ID_ISSUER_COUNTRY);
  }

  // Hide address difference fields when passport is selected (passports have no address)
  if (governmentIdType === GovernmentIdType.Passport) {
    hiddenFields.push(COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.IS_PHYSICAL_ADDRESS_DIFFERENT_THAN_ID_ADDRESS);
    hiddenFields.push(COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS);
    hiddenFields.push(COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.OTHER_REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS);
  } else {
    if (!isPhysicalAddressDifferentThanIdAddress) {
      hiddenFields.push(COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS);
    }

    if (reasonPhysicalAddressIsDifferentThanIdAddress !== ReasonPhysicalAddressIsDifferentThanIdAddressType.Other) {
      hiddenFields.push(COMPLEX_FIELD_KEYS.GOVERNMENT_ID_INFORMATION.OTHER_REASON_PHYSICAL_ADDRESS_IS_DIFFERENT_THAN_ID_ADDRESS);
    }
  }

  return hiddenFields;
}
